import { useRef, useState, type ReactNode } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import {
  beginUpload,
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  Chip,
  createUploadQueue,
  categoryPathLabel,
  createLandedUploadFinder,
  fileIndicatorsOf,
  errorMessage,
  HelpTip,
  isRetryableUploadError,
  relativePathOf,
  sendRoomUpload,
  skippedUploadsNotice,
  summarizeUploads,
  UploadSummary,
} from '@dataroom/shared';
import type {
  ClassificationStatus,
  FileIndicators,
  IngestFinalizeResponse,
  PaperworkKind,
  ProcessingStatus,
  RoomDocument,
  UploadJob,
  UploadOutcome,
  UploadQueue,
} from '@dataroom/shared';
import { invalidateRoomDocuments, useProcessingEstimate, useRoomDocuments } from '@/api/hooks';
import { UploadZone } from './UploadZone';
import { isNeedsReview } from '@/lib/classification-review';
import {
  processingQueueKey,
  processingSummary,
  type ProcessingQueueResponse,
} from '@/lib/processing-queue';
import {
  folderOfRelativePath,
  HIDDEN_TARGET_LABEL,
  isDifferentName,
  skipReportCsv,
  UNLISTED_TARGET_LABEL,
  uploadProgress,
  uploadStage,
  uploadSummary,
  type SkippedUpload,
  type UploadItemStatus,
} from '@/lib/upload-summary';
import { ClassificationStatusBadge } from './ClassificationStatusBadge';
import { DocumentPreviewModal } from './DocumentPreviewModal';

/** Client-side cap mirroring the ingest endpoint's 50 MiB ceiling — an
 * oversized file is refused up-front with a clear per-file error instead of
 * being sent to a guaranteed 4xx. */
const MAX_FILE_BYTES = 50 * 1024 * 1024;
const MAX_FILE_LABEL = '50 MB';
/** While a file of this panel's is queued, uploading, or finalized but not in
 * the room listing yet, the listing is re-read this often (the shared cadence
 * only starts once a fetched listing shows a document in flight). */
const UPLOAD_POLL_MS = 3_000;
/** A finalized document the listing still lacks after this long stops holding
 * the panel's poll open (never expected; this only bounds the poll). */
const LISTING_GRACE_MS = 60_000;

/** One file on the panel. `phase` is the client side of its journey; once
 * `uploaded`, the row reads the document's LIVE state from the polled listing. */
interface UploadItem {
  id: number;
  name: string;
  phase: 'queued' | 'uploading' | 'uploaded' | 'error';
  /** The finalized document's id (the finalize 201 is always `processing`;
   * classification is async), or the existing document for a skipped duplicate. */
  documentId?: string;
  /** When the finalize returned (bounds the "not in the listing yet" poll). */
  uploadedAt?: number;
  /** Failure detail (size cap, or the server's error message). */
  message?: string;
  /** The room already held these exact bytes: nothing was uploaded (ADR-0028). */
  skipped?: boolean;
  /** The picked file, kept until it is uploaded — so a failed one can be sent again. */
  file?: File;
  /** It failed for a passing reason (connection, busy server): "Try again" is offered. */
  retryable?: boolean;
  /** Where the file sat in a picked/dropped folder ("Leases/a.pdf"), and its
   * size — kept for the skip review (the server stores neither for a skip). */
  relativePath?: string;
  size?: number;
}

function UploadStatusBadge({
  status,
  processingStatus,
  indicators,
  paperworkKind,
  unpacked,
  onReview,
  onReviewRedaction,
  name,
}: {
  status: UploadItemStatus;
  processingStatus?: ProcessingStatus | null;
  indicators?: FileIndicators;
  paperworkKind?: PaperworkKind | null;
  unpacked?: boolean;
  onReview?: () => void;
  onReviewRedaction?: () => void;
  name: string;
}) {
  switch (status) {
    case 'queued':
      return (
        <Chip tone="neutral" icon="calendar">
          Queued
        </Chip>
      );
    case 'uploading':
      return (
        <Chip tone="processing" icon="upload">
          Uploading
        </Chip>
      );
    case 'uploaded':
      return (
        <Chip tone="neutral" icon="check">
          Uploaded
        </Chip>
      );
    case 'error':
      return (
        <Chip tone="error" icon="alert">
          Upload failed
        </Chip>
      );
    default:
      break;
  }
  // The same filing chip (+ buyer-copy marker) as the room's document table.
  return (
    <ClassificationStatusBadge
      status={status}
      processingStatus={processingStatus}
      indicators={indicators}
      paperworkKind={paperworkKind}
      unpacked={unpacked}
      onClick={onReview}
      actionLabel={onReview ? `review the classification of ${name}` : undefined}
      onReviewRedaction={onReviewRedaction}
      subject={name}
    />
  );
}

/**
 * One file's row on the upload status screen: its name, where it was filed,
 * any error, and its live badge. A needs-review result (low confidence /
 * couldn't classify) is a link into the same classification review the Data
 * room list opens.
 */
export function UploadItemRow({
  name,
  status,
  processingStatus,
  indicators,
  categoryPath,
  paperworkKind,
  unpacked,
  skipped,
  message,
  onReview,
  onReviewRedaction,
  onRetry,
}: {
  name: string;
  status: UploadItemStatus;
  /** The document's live processing step, once the polled listing has it. */
  processingStatus?: ProcessingStatus | null;
  /** The OCR / redaction indicators beside the filing chip (`fileIndicatorsOf`). */
  indicators?: FileIndicators;
  /** The category it was filed under (the listing's `categoryPath`). */
  categoryPath?: string[];
  paperworkKind?: PaperworkKind | null;
  unpacked?: boolean;
  /** An identical file was already in the room: nothing was uploaded. */
  skipped?: boolean;
  message?: string;
  /** Opens the classification review; offered only for a needs-review result. */
  onReview?: () => void;
  /** Opens the redaction review (its indicator reads "Review redaction" while it needs a person). */
  onReviewRedaction?: () => void;
  /** Sends a failed file again; offered only when the failure may pass. */
  onRetry?: () => void;
}) {
  const reviewable =
    !skipped && onReview !== undefined && isNeedsReview(asClassification(status));
  const filedUnder =
    !skipped &&
    uploadStage(status) === 'filed' &&
    categoryPath !== undefined &&
    categoryPath.length > 0
      ? categoryPathLabel(categoryPath)
      : null;
  return (
    <li className="flex items-center justify-between gap-3 text-sm">
      <span className="min-w-0">
        <span className="block truncate">{name}</span>
        {filedUnder ? (
          <span className="block truncate text-xs text-muted-foreground">
            Filed under {filedUnder}
          </span>
        ) : null}
      </span>
      <span className="flex shrink-0 items-center gap-2">
        {status === 'error' && message ? (
          <span className="text-xs text-destructive">{message}</span>
        ) : null}
        {skipped ? (
          <Chip tone="neutral" icon="minus">
            Already in room
          </Chip>
        ) : (
          <UploadStatusBadge
            status={status}
            processingStatus={processingStatus}
            indicators={indicators}
            paperworkKind={paperworkKind}
            unpacked={unpacked}
            name={name}
            onReview={reviewable ? onReview : undefined}
            onReviewRedaction={onReviewRedaction}
          />
        )}
        {reviewable ? (
          <button
            type="button"
            onClick={onReview}
            className="text-xs font-medium text-primary hover:underline"
          >
            Review
          </button>
        ) : null}
        {status === 'error' && onRetry ? (
          <button
            type="button"
            onClick={onRetry}
            aria-label={`Try uploading ${name} again`}
            className="text-xs font-medium text-primary hover:underline"
          >
            Try again
          </button>
        ) : null}
      </span>
    </li>
  );
}

function asClassification(status: UploadItemStatus): ClassificationStatus | undefined {
  switch (status) {
    case 'queued':
    case 'uploading':
    case 'uploaded':
    case 'error':
      return undefined;
    default:
      return status;
  }
}

/**
 * One skipped upload in the skip review: "<uploaded name> — identical to
 * <existing name>", the existing document's filing path and upload date, and a
 * "Different name" flag when the two names differ (the ones worth a look). The
 * existing name opens the document viewer.
 */
export function SkippedUploadRow({
  skip,
  existingLoading,
  onOpen,
}: {
  skip: SkippedUpload;
  /** The room listing has not arrived yet, so the identical document is not known. */
  existingLoading?: boolean;
  /** Opens the identical document in the viewer (offered when it can be viewed). */
  onOpen?: () => void;
}) {
  const e = skip.existing;
  const different = isDifferentName(skip);
  let target: ReactNode;
  if (e) {
    target = onOpen ? (
      <button
        type="button"
        onClick={onOpen}
        className="font-medium text-primary hover:underline"
        aria-label={`View ${e.name}`}
      >
        {e.name}
      </button>
    ) : (
      <span className="font-medium">{e.name}</span>
    );
  } else if (skip.hidden) {
    target = HIDDEN_TARGET_LABEL;
  } else {
    target = existingLoading ? 'a document already in the room (loading…)' : UNLISTED_TARGET_LABEL;
  }
  const details = e
    ? [
        e.categoryPath.length > 0 ? `Filed under ${categoryPathLabel(e.categoryPath)}` : 'Not filed yet',
        `added ${new Date(e.createdAt).toLocaleDateString()}`,
      ].join(' · ')
    : null;
  return (
    <li className="flex items-start justify-between gap-3 text-sm" data-different={different}>
      <span className="min-w-0">
        <span className="block break-words">
          <span>{skip.name}</span>
          <span className="text-muted-foreground"> — identical to </span>
          {target}
        </span>
        {details ? (
          <span className="block truncate text-xs text-muted-foreground">{details}</span>
        ) : null}
      </span>
      {different ? (
        <Chip tone="check" icon="alert">
          Different name
        </Chip>
      ) : null}
    </li>
  );
}

/** Saves the skip report as a CSV file (UTF-8 with a BOM so spreadsheet apps
 * read non-ASCII names correctly). */
function downloadSkipReport(skips: readonly SkippedUpload[]) {
  const blob = new Blob(['﻿', skipReportCsv(skips)], { type: 'text/csv;charset=utf-8' });
  const url = URL.createObjectURL(blob);
  try {
    const a = document.createElement('a');
    a.href = url;
    a.download = `skipped-uploads-${new Date().toISOString().slice(0, 10)}.csv`;
    document.body.appendChild(a);
    a.click();
    a.remove();
  } finally {
    // Revoke on the next tick so the click has started the download.
    setTimeout(() => URL.revokeObjectURL(url), 0);
  }
}

/** Which skipped uploads the panel lists: none (collapsed), all, or only the
 * ones whose name differs from the identical document's. */
type SkipView = 'hidden' | 'all' | 'different';

/** A row's status: its own client phase until the listing has the document,
 * then the document's live classification status. */
function rowStatus(it: UploadItem, live: RoomDocument | undefined): UploadItemStatus {
  if (it.phase !== 'uploaded') return it.phase;
  return live?.classificationStatus ?? 'uploaded';
}

/**
 * Admin document upload, BULK and CONTINUOUS. Accepts many files at once
 * (multi-select, multi-file drag-drop, or a whole folder) — and more at any
 * time, even while earlier files are still uploading or processing: every pick
 * joins ONE shared upload queue (`createUploadQueue`, @dataroom/shared: raw
 * bytes, 12 in flight, each file retried on its own after a passing failure).
 * Each file goes through the SAME single-file `ingest/finalize` pipeline
 * (dedup, storage, classification) the seller submission uses, and shows a
 * live row:
 *   Queued → Uploading → Uploaded → Classifying → Filed / Needs filing /
 *   Couldn't file — and, beside it, two small background indicators: OCR
 *   pending (page progress) / complete, Redaction pending / complete / needs review
 * with the category it was filed under, read from the polled room listing (the
 * one processing-step source of truth, ADR-0057). A summary line counts the
 * whole panel ("212 uploaded · 198 filed · 9 need filing · OCR 14 pending ·
 * Redaction 61 pending / 137 complete").
 */
export function UploadDocumentsCard({
  roomId,
  onReview,
  onReviewRedaction,
  viewAll,
}: {
  roomId: string;
  /** Open the classification review for a finalized document (by id). */
  onReview?: (documentId: string) => void;
  /** Open the redaction review for a finalized document (by id). */
  onReviewRedaction?: (documentId: string) => void;
  /** The "View all" link to the room's processing status page (beside the summary). */
  viewAll?: ReactNode;
}) {
  const qc = useQueryClient();
  const [items, setItems] = useState<UploadItem[]>([]);
  /** The session ended mid-batch (a 401): the queue stopped; say so once, above the rows. */
  const [sessionExpired, setSessionExpired] = useState(false);
  /** The skip review's list (collapsed by default), and the document open in the viewer. */
  const [skipView, setSkipView] = useState<SkipView>('hidden');
  const [previewId, setPreviewId] = useState<string | null>(null);
  // Read by the poll scheduler (outside render), so mirrored in a ref.
  const itemsRef = useRef<UploadItem[]>(items);
  itemsRef.current = items;
  const idRef = useRef(0);
  /** The shared queue every pick joins (created on first use), and each queued
   * file's "upload in progress" marker (the new-version banner waits for them). */
  const queueRef = useRef<UploadQueue | null>(null);
  const endsRef = useRef(new Map<number, () => void>());
  const roomRef = useRef(roomId);
  roomRef.current = roomId;

  // The room's documents, for each finalized row's LIVE state. Shares the cache
  // with the room table. On top of the shared cadence (fast while documents keep
  // settling), this panel re-reads the listing every 3 s while any of its own
  // files is not in the listing yet: the cadence alone only starts once a
  // fetched listing shows a document in flight, and the listing used to be
  // refreshed only after the WHOLE batch had uploaded.
  const documents = useRoomDocuments(roomId, {
    extraPoll: () => {
      const listed = new Set(
        (qc.getQueryData<RoomDocument[]>(['rooms', roomId, 'documents']) ?? []).map((d) => d.id),
      );
      const now = Date.now();
      const waiting = itemsRef.current.some(
        (it) =>
          it.phase === 'queued' ||
          it.phase === 'uploading' ||
          (it.phase === 'uploaded' &&
            it.documentId !== undefined &&
            !listed.has(it.documentId) &&
            now - (it.uploadedAt ?? now) < LISTING_GRACE_MS),
      );
      return waiting ? UPLOAD_POLL_MS : undefined;
    },
  });
  const liveById = new Map((documents.data ?? []).map((d) => [d.id, d] as const));

  function patch(id: number, next: Partial<UploadItem>) {
    setItems((prev) => prev.map((it) => (it.id === id ? { ...it, ...next } : it)));
  }

  /** The room's "Processing N…" indicator (server state) stops polling once
   * nothing runs: re-read it when an upload lands while it shows nothing running. */
  function wakeProcessingIndicator() {
    const room = roomRef.current;
    const cur = qc.getQueryData<ProcessingQueueResponse>(processingQueueKey(room, 'unfinished', 1));
    if (!cur || processingSummary(cur.counts).inProgress === 0) {
      void qc.invalidateQueries({ queryKey: ['rooms', room, 'processing'] });
    }
  }

  function uploadQueue(): UploadQueue {
    queueRef.current ??= createUploadQueue<IngestFinalizeResponse>({
      send: (file) => sendRoomUpload(roomRef.current, file),
      // A trial-cap refusal after a lost answer: is the file in the room after all?
      confirmLanded: createLandedUploadFinder(() => roomRef.current),
      onStart: (id) => patch(id, { phase: 'uploading' }),
      onSuccess: (id, res, { mayHaveLandedEarlier }) => {
        wakeProcessingIndicator();
        patch(id, {
          phase: 'uploaded',
          documentId: res.documentId,
          uploadedAt: Date.now(),
          // "Already in the room" after an attempt whose answer was lost means
          // that attempt landed — it is this upload, not a duplicate. After a
          // "busy" or a refusal (never stored), it is a real duplicate.
          skipped: res.skipped && !mayHaveLandedEarlier,
          file: undefined,
        });
      },
      // Every file type is accepted (ADR-0018 — finalize no longer 422s on file
      // type or a failed text extraction), so whatever comes back here is a
      // genuine error: surface the server's message as-is.
      onFailure: (id, err) =>
        patch(id, {
          phase: 'error',
          message: errorMessage(err) ?? 'Upload failed.',
          retryable: isRetryableUploadError(err),
        }),
      onSettled: (id) => {
        endsRef.current.get(id)?.();
        endsRef.current.delete(id);
      },
      onSessionExpired: () => setSessionExpired(true),
      // The queue is drained: refresh every folder view and the category tree once.
      onIdle: () => {
        void invalidateRoomDocuments(qc, roomRef.current);
        void qc.invalidateQueries({ queryKey: ['rooms', roomRef.current, 'categories'] });
      },
    });
    return queueRef.current;
  }

  function enqueue(jobs: UploadJob[]) {
    setSessionExpired(false);
    // Each file counts as an upload in progress (queued or sending) until it
    // ends, so the new-version banner says to reload after the uploads finish.
    for (const job of jobs) endsRef.current.set(job.id, beginUpload());
    uploadQueue().add(jobs);
  }

  function onFiles(files: File[]) {
    // Every file is a row up-front so the whole batch is visible at once,
    // newest batch on top. Ids are unique across batches, so a batch added
    // while another is running never touches the other's rows.
    const batch = files.map((file) => ({ id: (idRef.current += 1), file }));
    setItems((prev) => [
      ...batch.map(({ id, file }): UploadItem => {
        const known = { id, name: file.name, relativePath: relativePathOf(file), size: file.size };
        return file.size > MAX_FILE_BYTES
          ? {
              ...known,
              phase: 'error',
              message: `File is larger than the ${MAX_FILE_LABEL} limit.`,
            }
          : { ...known, phase: 'queued', file };
      }),
      ...prev,
    ]);
    enqueue(batch.filter(({ file }) => file.size <= MAX_FILE_BYTES));
  }

  /** "Try again" on a failed row: the same file joins the queue again. */
  function retry(id: number) {
    const file = itemsRef.current.find((it) => it.id === id)?.file;
    if (!file) return;
    patch(id, { phase: 'queued', message: undefined, retryable: undefined });
    enqueue([{ id, file }]);
  }

  const rows = items.map((it) => {
    const live = it.documentId ? liveById.get(it.documentId) : undefined;
    return { it, live, status: rowStatus(it, live) };
  });
  const counted = rows.filter((r) => !r.it.skipped);
  // The end-of-upload summary: every file the panel was given and how it ended.
  const outcomes: UploadOutcome[] = rows.map(({ it }) => ({
    name: it.name,
    kind:
      it.phase === 'error'
        ? 'failed'
        : it.phase === 'uploaded'
          ? it.skipped
            ? 'skipped'
            : 'added'
          : 'pending',
    ...(it.phase === 'error' && it.message ? { detail: it.message } : {}),
  }));
  // "Processing these will take about N minutes" (owner ask 2026-10-23): read the
  // room's estimate once the batch is done and something was added.
  const batch = summarizeUploads(outcomes);
  const processingEstimate = useProcessingEstimate(roomId, {
    enabled: batch.done && batch.added > 0,
  });
  // The skip review (owner ask 2026-10-15): each real duplicate beside the
  // document it matched. A "skipped" answer to a retry whose earlier attempt
  // landed is that upload, not a duplicate — `skipped` is false for it, so it
  // is counted as uploaded and never listed here.
  const skips: (SkippedUpload & { key: number; viewable: boolean })[] = rows
    .filter((r) => r.it.skipped)
    .map(({ it, live }) => ({
      key: it.id,
      name: it.name,
      folder: folderOfRelativePath(it.relativePath),
      size: it.size ?? null,
      hidden: it.documentId === undefined,
      existing: live
        ? {
            id: live.id,
            name: live.name,
            categoryPath: live.categoryPath,
            createdAt: live.createdAt,
            folderPath: live.folderPath,
          }
        : null,
      viewable: live?.downloadReady === true,
    }));
  const differentSkips = skips.filter(isDifferentName);
  const listedSkips = skipView === 'different' ? differentSkips : skips;
  const skippedCount = skips.length;
  const previewDoc = previewId ? liveById.get(previewId) : undefined;
  const progress = uploadProgress(
    counted.map((r) => uploadStage(r.status)),
    counted.map((r) => (r.live ? fileIndicatorsOf(r.live) : null)),
    skippedCount,
  );
  const percent = progress.total > 0 ? Math.round((progress.settled / progress.total) * 100) : 0;
  const skippedNotice = skippedUploadsNotice(skippedCount);
  // Owner ask 2026-10-12: a row leaves the panel's list once its file is FILED
  // (and so do skipped duplicates and deal paperwork, which are settled too) —
  // the list shows only what is still uploading, classifying, needing filing or
  // failed. The summary line above still counts the whole session.
  const shown = rows.filter(
    (r) => !r.it.skipped && !['filed', 'paperwork'].includes(uploadStage(r.status)),
  );
  const pruned = counted.length - shown.length;

  return (
    <Card>
      <CardHeader>
        <CardTitle>Add documents</CardTitle>
        <p className="text-sm text-muted-foreground">
          Add due diligence documents in batches of any size, whenever they're ready — drag in files
          or a folder, or browse. You can keep adding more while earlier files are still uploading
          or processing. The AI files each one into the right folder, and it appears in the
          document list below.
        </p>
      </CardHeader>
      <CardContent className="space-y-3">
        <UploadZone onFiles={onFiles} />
        {sessionExpired ? (
          <p role="alert" className="text-sm font-medium text-destructive">
            Your session expired — <a href="/login" className="underline">sign in</a> to continue.
            The files below were not sent.
          </p>
        ) : null}
        {/* One polite live region announces the batch as it settles (the chips
            themselves change silently), e.g. "4 of 5 files filed, 1 to check". */}
        <p role="status" aria-live="polite" className="sr-only">
          {uploadSummary(counted.map((r) => r.status))}
        </p>
        <UploadSummary outcomes={outcomes} processingEstimate={processingEstimate.data} />
        {progress.line ? (
          <div className="space-y-1.5">
            <div className="flex flex-wrap items-baseline justify-between gap-x-3 gap-y-1">
              <p className="text-sm font-medium" data-testid="upload-progress-line">
                {progress.line}
              </p>
              {viewAll}
            </div>
            {progress.total > 0 ? (
              <div
                className="h-1.5 w-full overflow-hidden rounded-full bg-muted"
                role="progressbar"
                aria-label={`${progress.settled} of ${progress.total} files filed or flagged`}
                aria-valuenow={percent}
                aria-valuemin={0}
                aria-valuemax={100}
              >
                <div
                  className="h-full rounded-full bg-primary transition-[width] duration-500"
                  style={{ width: `${percent}%` }}
                />
              </div>
            ) : null}
          </div>
        ) : null}
        {skippedNotice ? (
          <div className="space-y-1.5" data-testid="skipped-uploads">
            <p className="text-sm text-muted-foreground">{skippedNotice}</p>
            <p className="flex flex-wrap items-center gap-x-2 gap-y-1 text-sm">
              <button
                type="button"
                aria-expanded={skipView !== 'hidden'}
                onClick={() => setSkipView(skipView === 'hidden' ? 'all' : 'hidden')}
                className="font-medium text-primary hover:underline"
              >
                {skipView === 'hidden' ? 'Show' : 'Hide'} skipped ({skippedCount})
              </button>
              <HelpTip id="admin.upload.skipped" className="-ml-1" />
              <span aria-hidden="true" className="text-muted-foreground">
                ·
              </span>
              <button
                type="button"
                aria-pressed={skipView === 'different'}
                disabled={differentSkips.length === 0}
                onClick={() => setSkipView(skipView === 'different' ? 'all' : 'different')}
                className="font-medium text-primary hover:underline disabled:text-muted-foreground disabled:no-underline aria-pressed:underline"
              >
                only different names ({differentSkips.length})
              </button>
              <span aria-hidden="true" className="text-muted-foreground">
                ·
              </span>
              <button
                type="button"
                onClick={() => downloadSkipReport(skips)}
                className="font-medium text-primary hover:underline"
              >
                Download skip report (CSV)
              </button>
            </p>
            {skipView !== 'hidden' ? (
              <ul
                className="max-h-[28rem] space-y-1.5 overflow-y-auto pr-1"
                aria-label="Skipped uploads"
              >
                {listedSkips.map((s) => {
                  const existingId = s.existing?.id;
                  return (
                    <SkippedUploadRow
                      key={s.key}
                      skip={s}
                      existingLoading={documents.isPending}
                      onOpen={
                        existingId && s.viewable ? () => setPreviewId(existingId) : undefined
                      }
                    />
                  );
                })}
              </ul>
            ) : null}
          </div>
        ) : null}
        {previewDoc ? (
          <DocumentPreviewModal
            roomId={roomId}
            document={previewDoc}
            onClose={() => setPreviewId(null)}
          />
        ) : null}
        {pruned > 0 ? (
          <p className="text-xs text-muted-foreground" data-testid="upload-pruned-note">
            {pruned === 1
              ? '1 filed document has left this list'
              : `${pruned} filed documents have left this list`}{' '}
            — they are in the document list below, and their OCR and redaction show on the
            processing status page.
          </p>
        ) : null}
        {shown.length > 0 ? (
          <ul className="max-h-[28rem] space-y-1.5 overflow-y-auto pr-1" aria-label="Uploads">
            {shown.map(({ it, live, status }) => {
              const documentId = it.documentId;
              return (
                <UploadItemRow
                  key={it.id}
                  name={it.name}
                  status={status}
                  processingStatus={live?.processingStatus}
                  indicators={live ? fileIndicatorsOf(live) : undefined}
                  categoryPath={live?.categoryPath}
                  paperworkKind={live?.paperworkKind}
                  unpacked={live?.archive != null}
                  skipped={it.skipped}
                  message={it.message}
                  onReview={documentId && onReview ? () => onReview(documentId) : undefined}
                  onReviewRedaction={
                    documentId && onReviewRedaction
                      ? () => onReviewRedaction(documentId)
                      : undefined
                  }
                  onRetry={it.retryable && it.file ? () => retry(it.id) : undefined}
                />
              );
            })}
          </ul>
        ) : null}
      </CardContent>
    </Card>
  );
}
